import { SavingsRates } from 'features/accounts/helpers/savingsRates.helpers';
import Disclosures from 'features/v2a/receiver/components/Disclosure/Disclosures';
import {
  appLinks,
  socialLinks,
  termsLinks,
} from 'features/v2a/receiver/helpers/V2A.helpers';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Linking, StyleSheet, View } from 'react-native';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import IconButton from 'shared/components/vxl/icon/IconButton/IconButton';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import VaroLogo from 'shared/components/vxl/VaroLogo/VaroLogo';
import theme from 'shared/styles/theme';

interface ReceiverFooterProps {
  savingsRates?: SavingsRates;
  isSavingsRatesUnavailable?: boolean;
  onRetrySavingsRates?: () => void;
}

const ReceiverFooter = ({
  savingsRates,
  isSavingsRatesUnavailable,
  onRetrySavingsRates,
}: ReceiverFooterProps) => {
  const { t: translateText } = useTranslation('moveMoney', {
    keyPrefix: 'receiverLanding',
  });

  const socialIcons = Object.keys(socialLinks).map((key) => ({
    key,
    label: key,
  }));

  const footerLinks = [
    { text: translateText('marketing.termsAndCondition'), key: 'TERMS' },
    { text: translateText('marketing.privacyPolicy'), key: 'PRIVACY' },
    { text: translateText('marketing.legalDocuments'), key: 'LEGAL' },
  ];

  return (
    <View style={styles.footerContainer}>
      <View style={styles.footerContent}>
        <VaroLogo width={65} height={24} color="white" />
        <View style={styles.socialIconsRow}>
          {socialIcons.map(({ key, label }) => (
            <IconButton
              key={key}
              iconKey={key}
              iconSize="SM"
              focusedColor={theme.color.background.primary}
              iconColor={theme.color.background.primary}
              hoverColor={theme.color.background.secondary}
              iconButtonAccessibilityLabel={label}
              iconOnPress={() =>
                void Linking.openURL(socialLinks[key] as string)
              }
            />
          ))}
        </View>

        <View style={styles.appDownloadButtons}>
          {Object.entries(appLinks).map(([key, url]) => (
            <IconButton
              key={key}
              iconKey={key}
              iconSize={key === 'APPSTORE' ? 150 : 168}
              focusedColor={theme.color.background.primary}
              iconColor={theme.color.background.primary}
              hoverColor={theme.color.background.secondary}
              iconButtonAccessibilityLabel={
                key === 'APPSTORE'
                  ? 'Get it on App Store'
                  : 'Get it on Google Play'
              }
              iconOnPress={() => void Linking.openURL(url)}
            />
          ))}
        </View>
      </View>

      <View style={styles.footerTermsDisclosure}>
        <View style={styles.footerInfo}>
          <View style={styles.footerColumn}>
            <Icon
              iconKey="FDIC"
              iconSize={100}
              iconColor={theme.color.text.inverse}
              iconAccessibilityLabel="FDIC Logo"
            />
            <BodyBaseText style={styles.footerText}>
              {translateText('marketing.varoCopyrightLabel', {
                year: new Date().getFullYear().toString(),
              })}
            </BodyBaseText>
          </View>
          <View style={styles.footerColumn}>
            {footerLinks.map(({ text, key }) => (
              <BodyBaseText
                key={key}
                style={styles.footerLink}
                onPress={() => void Linking.openURL(termsLinks[key] as string)}
              >
                {text}
              </BodyBaseText>
            ))}
          </View>
        </View>
        <View style={styles.hairLineDivider}>
          <HairLineDivider />
        </View>
        <Disclosures
          savingsRates={savingsRates}
          isSavingsRatesUnavailable={isSavingsRatesUnavailable}
          onRetrySavingsRates={onRetrySavingsRates}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  appDownloadButtons: {
    flexDirection: 'row',
    gap: theme.spacing.xs,
    justifyContent: 'flex-start',
  },
  footerColumn: {
    flex: 1,
  },
  footerContainer: {
    backgroundColor: theme.color.interactive.black,
  },
  footerContent: {
    padding: theme.spacing.xl,
  },
  footerInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  footerLink: {
    color: theme.color.interactive.inverse,
    marginBottom: theme.spacing.s,
  },
  footerTermsDisclosure: {
    backgroundColor: '#7676762E',
    padding: theme.spacing.xl,
  },
  footerText: {
    color: theme.color.interactive.inverse,
    lineHeight: 18,
    marginTop: theme.spacing.s,
  },
  hairLineDivider: {
    backgroundColor: theme.color.interactive.inverse,
    marginTop: theme.spacing.l,
  },
  socialIconsRow: {
    flexDirection: 'row',
    gap: theme.spacing.xl,
    justifyContent: 'flex-start',
    marginHorizontal: theme.spacing.s,
    marginVertical: theme.spacing.xl,
  },
});

export default ReceiverFooter;
